/**
 * PatientOnboardingFlow.jsx
 * Refactored: CSS Modules + responsive layout + localStorage via usePatientStorage hook.
 *
 * Flow:
 *  Welcome
 *   → Patient Type (Existing | New)
 *     → EXISTING: Mobile → OTP → Verified → /symptoms
 *     → NEW:      Name+Mobile → OTP → Age → Routine → Marital
 *                 → Height → Weight → Summary → /symptoms
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Globalimages } from "../components/Imagesreusable";
import { usePatientStorage } from "../hooks/usePatientStorage";
import styles from "../styles/patientinfo.module.css";
import { ExistingPatientIcon, NewPatientIcon } from "../config/icons";
import SummaryScreen from "../components/Reusablesteps/Summaryscreen";

/* ─── Screens enum ───────────────────────────────────────────────── */
const SCREEN = {
  WELCOME:      "welcome",
  TYPE:         "type",
  EX_MOBILE:    "ex_mobile",
  EX_OTP:       "ex_otp",
  EX_VERIFIED:  "ex_verified",
  NEW_MOBILE:   "new_mobile",
  NEW_OTP:      "new_otp",
  NEW_STEP:     "new_step",
  SUMMARY:      "summary",
};

/* ─── New-patient steps config ───────────────────────────────────── */
const NEW_STEPS = [
  {
    id: "age_group", type: "chips",
    question: "What's your age group?",
    desc: "This helps us personalize your experience",
    summaryLabel: "Age Group", summaryIcon: "🎂",
  },
  {
    id: "daily_routine", type: "chips",
    question: "Nice 👍 What best describes your daily routine?",
    desc: "This helps us personalize your experience",
    summaryLabel: "Lifestyle", summaryIcon: "🏢",
  },
  {
    id: "marital_status", type: "chips",
    question: "If you're comfortable sharing,",
    text: "what's your marital status?",
    desc: "This helps us personalize your experience",
    summaryLabel: "Marital Status", summaryIcon: "💍",
  },
  {
    id: "height", type: "slider",
    question: "Let's personalise your experience 📐",
    description: "What is your height?",
    sliderLabel: "Height", sliderMin: 3, sliderMax: 7, sliderStep: 0.1,
    sliderDefault: 5.3, sliderUnit: "Ft.", sliderDecimals: 1,
    sliderTicks: ["3", "4", "5", "6", "7"],
    summaryLabel: "Height", summaryIcon: "📏",
  },
  {
    id: "weight", type: "slider",
    question: "And your weight?",
    sliderLabel: "Weight", sliderMin: 30, sliderMax: 150, sliderStep: 1,
    sliderDefault: 58, sliderUnit: "Kg", sliderDecimals: 0,
    sliderTicks: ["30", "60", "90", "120", "150"],
    summaryLabel: "Weight", summaryIcon: "⚖️",
  },
];

/* ─── Chip data ──────────────────────────────────────────────────── */
const CHIP_DATA = {
  age_group: [
    { label: "18–25", img: Globalimages.image1 },
    { label: "26–35", img: Globalimages.image2 },
    { label: "36–45", img: Globalimages.image3 },
    { label: "46–55", img: Globalimages.image4 },
    { label: "56+",   img: Globalimages.image5 },
  ],
  daily_routine: [
    { label: "Working",   img: Globalimages.image10 },
    { label: "Homemaker", img: Globalimages.image11 },
    { label: "Student",   img: Globalimages.image12 },
  ],
  marital_status: [
    { label: "Single",            img: Globalimages.image6 },
    { label: "Married",           img: Globalimages.image7 },
    { label: "Divorced",          img: Globalimages.image8 },
    { label: "Prefer not to say", img: Globalimages.image9 },
  ],
};

/* ══════════════════════════════════════════════════════════════════
   ICON COMPONENTS
══════════════════════════════════════════════════════════════════ */
const LockIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
    stroke="var(--clr-primary)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x={3} y={11} width={18} height={11} rx={2} />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const CheckIcon = ({ color = "#fff" }) => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const ArrowRight = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none"
    stroke="#fff" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const EditIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
    stroke="var(--clr-primary)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

/* ══════════════════════════════════════════════════════════════════
   SHARED SUB-COMPONENTS
══════════════════════════════════════════════════════════════════ */

/** Header bar with logo */
const HeaderBar = () => (
  <div className={styles.headerBar}>
    <div className={styles.logoText}>
      <img src={Globalimages.logo} alt="Avexa Logo" className={styles.logo} />
    </div>
  </div>
);

/** Progress dots */
const ProgressDots = ({ current, total }) => (
  <div className={styles.progressDots}>
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className={[
          styles.dot,
          i < current  ? styles.dotDone    :
          i === current ? styles.dotActive  :
                          styles.dotPending,
        ].join(" ")}
        style={i === current ? { width: 20 } : {}}
      />
    ))}
  </div>
);

/** Secure info footer */
const SecureInfo = () => (
  <div className={styles.secureInfo}>
    <LockIcon />
    <span>Your information is private and secure</span>
  </div>
);

/** Primary CTA button */
const PrimaryBtn = ({ label, onClick, disabled, icon }) => (
  <button
    className={styles.primaryBtn}
    onClick={onClick}
    disabled={disabled}
  >
    {label} {icon}
  </button>
);

/* ══════════════════════════════════════════════════════════════════
   SCREEN: Welcome
══════════════════════════════════════════════════════════════════ */
const WelcomeScreen = ({ onStart, onSkip }) => (
  <div className={`${styles.stepWrapper} ${styles.welcomeWrapper}`}>
    <div className={styles.centreArea} style={{ gap: 28 }}>
      <div className={styles.avatarBubble}>👋</div>

      <div className={styles.textCenter}>
        <h2 className={styles.question} style={{ fontSize: 24, marginBottom: 10 }}>
          Hi 👋 I'm your symptom checker assistant.
        </h2>
        <p className={styles.desc} style={{ fontSize: 15 }}>
          Let's get to know you better{" "}
          <span style={{ color: "var(--clr-muted)" }}>(takes 20 seconds)</span>
        </p>
      </div>

      <div className={styles.welcomeActions}>
        <PrimaryBtn label="Let's start" onClick={onStart} icon={<ArrowRight />} />
        <button className={styles.skipBtn} onClick={onSkip} style={{ marginTop: 8, width: "100%" }}>
          Skip to Home
        </button>
      </div>
    </div>
    <SecureInfo />
  </div>
);

/* ══════════════════════════════════════════════════════════════════
   SCREEN: Patient Type
══════════════════════════════════════════════════════════════════ */
const TypeCard = ({ label, emoji, desc, onClick }) => (
  <button className={styles.typeCard} onClick={onClick}>
    <span className={styles.typeCardEmoji}>{emoji}</span>
    <div className={styles.typeCardBody}>
      <div className={styles.typeCardLabel}>{label}</div>
      <div className={styles.typeCardDesc}>{desc}</div>
    </div>
    <div className={styles.typeCardArrow}>
      <ArrowRight />
    </div>
  </button>
);

const PatientTypeScreen = ({ onSelect }) => (
  <div className={styles.stepWrapper}>
    <div className={styles.centreArea}>
      <div className={styles.textCenter}>
        <h2 className={styles.question}>Are you a new or existing patient?</h2>
        <p className={styles.desc}>This helps us personalize your experience</p>
      </div>
      <div className={styles.typeCardsRow}>
        <TypeCard
          label="Existing Patient" emoji={<ExistingPatientIcon />} desc="I've visited before"
          onClick={() => onSelect("existing")}
        />
        <TypeCard
          label="New Patient" emoji={<NewPatientIcon />} desc="First time here"
          onClick={() => onSelect("new")}
        />
      </div>
    </div>
    <SecureInfo />
  </div>
);

/* ══════════════════════════════════════════════════════════════════
   SCREEN: Mobile Input
══════════════════════════════════════════════════════════════════ */
const MobileInputScreen = ({
  isNew, nameVal, phoneVal,
  onNameChange, onPhoneChange, onNext,
  totalSteps, currentStep,
}) => {
  const phoneOk = phoneVal.replace(/\D/g, "").length === 10;
  const canProceed = isNew ? (nameVal.trim().length > 0 && phoneOk) : phoneOk;

  return (
    <div className={styles.stepWrapper}>
      <ProgressDots current={currentStep} total={totalSteps} />
      <div className={styles.centreArea}>
        <div className={styles.textCenter}>
          <h2 className={styles.question}>
            {isNew ? "Basic Info 😊" : "Welcome back! 👋"}
          </h2>
          <p className={styles.desc}>
            {isNew
              ? "Where should we send your personalised health insights?"
              : "Enter your mobile number to continue"}
          </p>
        </div>

        <div className={styles.mobileInputGroup}>
          {isNew && (
            <input
              className={styles.inputField}
              type="text"
              placeholder="Enter your name"
              value={nameVal}
              onChange={e => onNameChange(e.target.value)}
            />
          )}
          <div className={styles.phoneWrap}>
            <span className={styles.countryCode}>+91</span>
            <input
              className={styles.phoneInput}
              type="tel"
              placeholder="10 digit mobile number"
              maxLength={10}
              value={phoneVal}
              onChange={e => onPhoneChange(e.target.value.replace(/\D/g, ""))}
            />
          </div>
          <PrimaryBtn
            label="Send OTP"
            onClick={onNext}
            disabled={!canProceed}
            icon={<ArrowRight />}
          />
        </div>
      </div>
      <SecureInfo />
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCREEN: OTP
══════════════════════════════════════════════════════════════════ */
const OTPScreen = ({ phone, onVerify, onResend, totalSteps, currentStep }) => {
  const [otp, setOtp]     = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(42);
  const [error, setError] = useState("");
  const refs = useRef([]);

  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[i] = val;
    setOtp(next);
    setError("");
    if (val && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6).split("");
    const next = [...otp];
    paste.forEach((d, i) => { next[i] = d; });
    setOtp(next);
    refs.current[Math.min(paste.length, 5)]?.focus();
  };

  const filled = otp.join("").length === 6;

  const handleResend = () => {
    setTimer(42);
    setOtp(["", "", "", "", "", ""]);
    setError("");
    onResend?.();
  };

  return (
    <div className={styles.stepWrapper}>
      <ProgressDots current={currentStep} total={totalSteps} />
      <div className={styles.centreArea}>
        <div className={styles.textCenter}>
          <h2 className={styles.question} style={{ marginBottom: 8 }}>
            Enter Verification Code
          </h2>
          <p className={styles.desc}>
            We sent a 6-digit OTP to{" "}
            <strong style={{ color: "var(--clr-text)" }}>+91 {phone}</strong>
          </p>
        </div>

        <div className={styles.otpBoxRow} onPaste={handlePaste}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={el => refs.current[i] = el}
              type="tel"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              className={`${styles.otpBox} ${digit ? styles.filled : ""}`}
            />
          ))}
        </div>

        {error && <p className={styles.otpError}>{error}</p>}

        <p className={styles.resendText}>
          Didn't receive it?{" "}
          {timer > 0 ? (
            <span>
              Resend in{" "}
              <strong style={{ color: "var(--clr-primary)" }}>{timer}s</strong>
            </span>
          ) : (
            <button className={styles.textBtn} onClick={handleResend}>
              Resend
            </button>
          )}
        </p>

        <div className={styles.otpActions}>
          <PrimaryBtn
            label="Verify & Confirm"
            onClick={() => filled && onVerify(otp.join(""))}
            disabled={!filled}
          />
        </div>
      </div>
      <SecureInfo />
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCREEN: Chip Select
══════════════════════════════════════════════════════════════════ */
const ChipSelectScreen = ({
  questionId, question, desc, text, onSelect,
  totalSteps, currentStep,
}) => {
  const chips = CHIP_DATA[questionId] || [];
  const [sel, setSel] = useState(null);

  const handlePick = (val) => {
    setSel(val);
    setTimeout(() => onSelect(val), 200);
  };

  return (
    <div className={styles.stepWrapper}>
      <ProgressDots current={currentStep} total={totalSteps} />
      <div className={styles.centreArea}>
        <div className={styles.textCenter}>
          <h2 className={styles.question}>
            {question}
            {text ? <><br />{text}</> : null}
          </h2>
          {desc && <p className={styles.desc}>{desc}</p>}
        </div>

        <div className={styles.chipGrid}>
          {chips.map(({ label, img }) => {
            const isSelected = sel === label;
            return (
              <button
                key={label}
                onClick={() => handlePick(label)}
                className={`${styles.chip} ${isSelected ? styles.selected : ""}`}
              >
                {isSelected && (
                  <div className={styles.chipCheckBadge}>
                    <CheckIcon />
                  </div>
                )}
                <img src={img} alt="" className={styles.chipImage} />
                <span className={styles.chipLabel}>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <SecureInfo />
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCREEN: Slider
══════════════════════════════════════════════════════════════════ */
const SliderScreen = ({
  question, description,
  sliderLabel, sliderMin, sliderMax, sliderStep,
  sliderDefault, sliderUnit, sliderDecimals, sliderTicks,
  onNext, totalSteps, currentStep, isLast,
}) => {
  const [val, setVal] = useState(sliderDefault ?? sliderMin ?? 0);
  const dec = sliderDecimals ?? 0;
  const pct = ((val - sliderMin) / (sliderMax - sliderMin)) * 100;

  return (
    <div className={styles.stepWrapper}>
      <ProgressDots current={currentStep} total={totalSteps} />
      <div className={styles.centreArea} style={{ marginTop: 40 }}>
        <h2 className={styles.question}>{question}</h2>
        {description && <p className={styles.question}>{description}</p>}

        <div className={styles.sliderGroup}>
          <div className={styles.sliderHeader}>
            <span className={styles.sliderLabelText}>{sliderLabel}</span>
            <span className={styles.sliderValue}>
              {Number(val).toFixed(dec)} {sliderUnit}
            </span>
          </div>

          <div className={styles.sliderTrack}>
            <div
              className={styles.sliderFill}
              style={{ width: `${pct}%` }}
            />
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              step={sliderStep ?? 1}
              value={val}
              onChange={e => setVal(parseFloat(e.target.value))}
              className={styles.sliderInput}
            />
          </div>

          {sliderTicks?.length > 0 && (
            <div className={styles.sliderTicks}>
              {sliderTicks.map(t => (
                <span key={t} className={styles.sliderTickLabel}>{t}</span>
              ))}
            </div>
          )}

          <div className={styles.sliderSaveRow}>
            <button
              className={styles.saveBtn}
              onClick={() => onNext(val)}
            >
              {isLast ? "Submit" : "Save"}
            </button>
          </div>
        </div>
      </div>
      <SecureInfo />
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCREEN: Summary
══════════════════════════════════════════════════════════════════ */
const SummaryRow = ({ icon, label, value, onEdit }) => (
  <div className={styles.summaryRow}>
    <div className={styles.summaryRowLeft}>
      <span className={styles.summaryRowIcon}>{icon}</span>
      <div>
        <div className={styles.summaryRowMeta}>{label}</div>
        <div className={styles.summaryRowValue}>{value}</div>
      </div>
    </div>
    <button className={styles.editBtn} onClick={onEdit}>
      <EditIcon />
    </button>
  </div>
);

// const SummaryScreen = ({ name, initials, phone, items, onEdit, onConfirm }) => (
//   <div className={styles.stepWrapper}>
//     <div className={styles.centreArea} style={{ gap: 16 }}>
//       <div className={styles.textCenter}>
//         <div style={{
//           width: 60, height: 60, borderRadius: "50%",
//           background: "var(--clr-primary-bg)",
//           margin: "0 auto 12px",
//           display: "flex", alignItems: "center", justifyContent: "center",
//           fontSize: 28,
//         }}>🎉</div>
//         <h2 className={styles.question}>Here's your summary</h2>
//         <p className={styles.desc}>Review your details before we proceed</p>
//       </div>

//       <div className={styles.summaryList}>
//         {/* Contact row */}
//         <div className={styles.summaryContactRow}>
//           <div className={styles.summaryRowLeft}>
//             {/* <span className={styles.summaryRowIcon}>📱</span> */}
//             <div className={styles.avatar}>{initials}</div>
//             <div>
//               <div className={styles.summaryContactMeta}>Contact</div>
//               <div className={styles.summaryContactValue}>
//                 {name && `${name} · `}+91 {phone}
//               </div>
//             </div>
//           </div>
//           <div className={styles.summaryCheckBadge}>
//             <CheckIcon />
//           </div>
//         </div>

//         {items.map((item, i) => (
//           <SummaryRow
//             key={i}
//             icon={item.icon}
//             label={item.label}
//             value={item.value}
//             onEdit={() => onEdit(i)}
//           />
//         ))}
//       </div>

//       <div className={styles.summaryActions}>
//         <PrimaryBtn
//           label="Confirm & Continue"
//           onClick={onConfirm}
//           icon={<ArrowRight />}
//         />
//       </div>
//     </div>
//     <SecureInfo />
//   </div>
// );

/* ══════════════════════════════════════════════════════════════════
   SCREEN: Verified (existing patient)
══════════════════════════════════════════════════════════════════ */
const VerifiedScreen = ({ name, onContinue }) => (
  <div className={styles.stepWrapper}>
    <div className={styles.centreArea}>
      <div className={styles.verifiedBubble}>✅</div>
      <div className={styles.textCenter}>
        <h2 className={styles.question}>
          Welcome back{name ? `, ${name}` : ""}!
        </h2>
        <p className={styles.desc}>Your identity has been verified successfully.</p>
      </div>
      <div style={{ width: "100%", maxWidth: 320 }}>
        <PrimaryBtn
          label="Go to Symptoms"
          onClick={onContinue}
          icon={<ArrowRight />}
        />
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════
   ROOT ORCHESTRATOR
══════════════════════════════════════════════════════════════════ */
export default function PatientOnboardingFlow({
  onComplete,
  onSkip,
  redirectPath = "/symptoms",
}) {
  /* ── localStorage-backed persona state ─────────────────────── */
  const [persona, mergePersona] = usePatientStorage();

  /* ── Local UI state ─────────────────────────────────────────── */
  const [screen,    setScreen]    = useState(SCREEN.WELCOME);
  const [name,      setName]      = useState(persona.name    || "");
  const [phone,     setPhone]     = useState(persona.mobile  || "");
  const [answers,   setAnswers]   = useState(() => {
    // Pre-fill chip/slider answers from stored persona
    const stored = {};
    NEW_STEPS.forEach(step => {
      if (persona[step.id] !== undefined) stored[step.id] = persona[step.id];
    });
    return stored;
  });
  const [stepIndex, setStepIndex] = useState(0);

  /* ── Navigation helper ──────────────────────────────────────── */
  const goTo = useCallback(sc => setScreen(sc), []);

  /* ── Existing flow ──────────────────────────────────────────── */
  const handleExistingVerify = () => {
    mergePersona({ mobile: phone });
    goTo(SCREEN.EX_VERIFIED);
  };

  const handleExistingComplete = () => {
    onComplete?.({ answers: { phone } });
    window.location.href = redirectPath;
  };

  /* ── New patient flow ───────────────────────────────────────── */
  const handleNewVerify = () => {
    mergePersona({ name, mobile: phone });
    setStepIndex(0);
    goTo(SCREEN.NEW_STEP);
  };

  const advanceStep = (id, val) => {
    const updated = { ...answers, [id]: val };
    setAnswers(updated);
    mergePersona({ [id]: val });
    if (stepIndex < NEW_STEPS.length - 1) {
      setStepIndex(i => i + 1);
    } else {
      goTo(SCREEN.SUMMARY);
    }
  };

 const handleSummaryEdit = (itemIndex) => {
  if (itemIndex === -1) {
    setStepIndex(0); // edit all
  } else {
    setStepIndex(itemIndex);
  }
  goTo(SCREEN.NEW_STEP);
};

  const handleSummaryConfirm = () => {
    const finalData = { name, phone, ...answers };
    mergePersona(finalData);
    onComplete?.({ answers: finalData });
    window.location.href = redirectPath;
  };

  /* ── Summary items builder ──────────────────────────────────── */
  const buildSummaryItems = () =>
    NEW_STEPS.map(step => {
      const val = answers[step.id];
      let display = val;
      if (step.type === "slider") {
        const num = val ?? step.sliderDefault ?? step.sliderMin ?? 0;
        display = `${Number(num).toFixed(step.sliderDecimals ?? 0)} ${step.sliderUnit}`;
      }
      return { label: step.summaryLabel, icon: step.summaryIcon, value: display || "—" };
    });

  /* ── Progress dot counts ────────────────────────────────────── */
  const existingTotal = 3;
  const newTotal      = 2 + NEW_STEPS.length + 1;

  const existingStep =
    screen === SCREEN.EX_MOBILE ? 0 :
    screen === SCREEN.EX_OTP    ? 1 : 2;

  const newStep =
    screen === SCREEN.NEW_MOBILE ? 0 :
    screen === SCREEN.NEW_OTP    ? 1 :
    screen === SCREEN.NEW_STEP   ? 2 + stepIndex :
                                   2 + NEW_STEPS.length;

  const currentStepConfig = NEW_STEPS[stepIndex];

  const heightInCm = answers.height
  ? Math.round(answers.height * 30.48)
  : persona.height; 

  /* ── Screen renderer ────────────────────────────────────────── */
  const renderContent = () => {
    switch (screen) {

      case SCREEN.WELCOME:
        return (
          <WelcomeScreen
            onStart={() => goTo(SCREEN.TYPE)}
            onSkip={onSkip}
          />
        );

      case SCREEN.TYPE:
        return (
          <PatientTypeScreen
            onSelect={type => {
              goTo(type === "existing" ? SCREEN.EX_MOBILE : SCREEN.NEW_MOBILE);
            }}
          />
        );

      /* ── Existing patient ── */
      case SCREEN.EX_MOBILE:
        return (
          <MobileInputScreen
            isNew={false}
            nameVal={name}   phoneVal={phone}
            onNameChange={setName} onPhoneChange={setPhone}
            onNext={() => goTo(SCREEN.EX_OTP)}
            totalSteps={existingTotal} currentStep={existingStep}
          />
        );

      case SCREEN.EX_OTP:
        return (
          <OTPScreen
            phone={phone}
            onVerify={handleExistingVerify}
            onResend={() => {}}
            totalSteps={existingTotal} currentStep={existingStep}
          />
        );

      case SCREEN.EX_VERIFIED:
        return <VerifiedScreen name={name} onContinue={handleExistingComplete} />;

      /* ── New patient ── */
      case SCREEN.NEW_MOBILE:
        return (
          <MobileInputScreen
            isNew={true}
            nameVal={name}   phoneVal={phone}
            onNameChange={setName} onPhoneChange={setPhone}
            onNext={() => goTo(SCREEN.NEW_OTP)}
            totalSteps={newTotal} currentStep={newStep}
          />
        );

      case SCREEN.NEW_OTP:
        return (
          <OTPScreen
            phone={phone}
            onVerify={handleNewVerify}
            onResend={() => {}}
            totalSteps={newTotal} currentStep={newStep}
          />
        );

      case SCREEN.NEW_STEP: {
        if (!currentStepConfig) return null;

        if (currentStepConfig.type === "chips") {
          return (
            <ChipSelectScreen
              questionId={currentStepConfig.id}
              question={currentStepConfig.question}
              desc={currentStepConfig.desc}
              text={currentStepConfig.text}
              onSelect={val => advanceStep(currentStepConfig.id, val)}
              totalSteps={newTotal} currentStep={newStep}
            />
          );
        }

        if (currentStepConfig.type === "slider") {
          return (
            <SliderScreen
              {...currentStepConfig}
              onNext={val => advanceStep(currentStepConfig.id, val)}
              isLast={stepIndex === NEW_STEPS.length - 1}
              totalSteps={newTotal} currentStep={newStep}
            />
          );
        }
        return null;
      }

      // case SCREEN.SUMMARY:
      //   return (
      //     <SummaryScreen
      //       name={name}
      //       initials={name ? name.split(" ").map(n => n[0]).join("").toUpperCase() : "U"}
      //       phone={phone}
      //       items={buildSummaryItems()}
      //       onEdit={handleSummaryEdit}
      //       onConfirm={handleSummaryConfirm}
      //     />
      //   );

      case SCREEN.SUMMARY:
      return (
        <></>
        // <SummaryScreen
        //   name={name || persona.name}
        //   initials={
        //     (name || persona.name)
        //       ? (name || persona.name)
        //           .split(" ")
        //           .map(n => n[0])
        //           .join("")
        //           .toUpperCase()
        //       : "U"
        //   }
        //   phone={phone || persona.mobile}
        //   items={buildSummaryItems()}
        //   onEdit={handleSummaryEdit}
        //   onConfirm={handleSummaryConfirm}
        //   confirmLink={redirectPath}


        //   patient={persona}

        //   heightCm={heightInCm}
        //   weightKg={answers.weight || persona.weight}
        //   hideHeader={true}
        // />
      );

      default:
        return null;
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <HeaderBar />
        {renderContent()}
      </div>
    </div>
  );
}
